/**
 * Recent Items API Integration
 * Handles recent items tracking and retrieval
 */

import axiosClient from '../axiosClient';
import {
  RecentItem,
  TrackAccessRequest,
  TrackAccessResponse,
  QueryRecentParams,
} from '../types/RecentTypes';

/**
 * Recent Items API endpoints
 */
export const recentsApi = {
  /**
   * Track item access
   */
  trackAccess: async (data: TrackAccessRequest): Promise<TrackAccessResponse> => {
    try {
      const response = await axiosClient.post('/recents/track', data);
      console.log('✅ Access tracked:', data.itemType, data.itemId);
      return response.data;
    } catch (error) {
      console.error('❌ Error tracking access:', error);
      throw error;
    }
  },

  /**
   * Get all recent items
   */
  getRecentItems: async (params?: QueryRecentParams): Promise<RecentItem[]> => {
    try {
      const response = await axiosClient.get('/recents', {
        params: {
          limit: params?.limit || 20,
          type: params?.type,
        },
      });
      console.log('✅ Fetched recent items - Count:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching recent items:', error);
      throw error;
    }
  },

  /**
   * Get recent items for a specific workspace
   */
  getWorkspaceRecentItems: async (
    workspaceId: string,
    params?: QueryRecentParams,
  ): Promise<RecentItem[]> => {
    try {
      const response = await axiosClient.get(
        `/workspaces/${workspaceId}/recents`,
        {
          params: {
            limit: params?.limit || 20,
          },
        },
      );
      console.log(
        '✅ Fetched workspace recent items:',
        workspaceId,
        '- Count:',
        response.data.length,
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching workspace recent items:', error);
      throw error;
    }
  },

  /**
   * Get most popular items (by access count)
   */
  getMostPopularItems: async (
    params?: QueryRecentParams,
  ): Promise<RecentItem[]> => {
    try {
      const response = await axiosClient.get('/recents/popular', {
        params: {
          limit: params?.limit || 10,
          type: params?.type,
        },
      });
      console.log('✅ Fetched popular items - Count:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching popular items:', error);
      throw error;
    }
  },

  /**
   * Remove a specific recent item
   */
  removeRecentItem: async (recentItemId: string): Promise<void> => {
    try {
      await axiosClient.delete(`/recents/${recentItemId}`);
      console.log('✅ Recent item removed:', recentItemId);
    } catch (error) {
      console.error('❌ Error removing recent item:', error);
      throw error;
    }
  },

  /**
   * Clear all recent items
   */
  clearRecentItems: async (params?: QueryRecentParams): Promise<void> => {
    try {
      await axiosClient.delete('/recents', {
        params: {
          type: params?.type,
        },
      });
      console.log('✅ Recent items cleared');
    } catch (error) {
      console.error('❌ Error clearing recent items:', error);
      throw error;
    }
  },
};

export default recentsApi;
